// OrderReport.tsx — layout updated to match source.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { defaulSetDateFilter } from "../../../../formHelper/dateFilter";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Loader from "../../../../formHelper/Loader";
import Table from "../../../../formHelper/list";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";

type Id = number;
interface ProductRow {
  [key: string]: any;
}

interface PendingQuotationFormData {
  current_follow_date: string;
  reason: string;
  reason_status_id: string | number | null;
}

interface Quotation {
  [key: string]: any;
  id: number;
  quotation_details?: ProductRow[];
  company_details?: { company_name?: string };
  branch_details?: { name?: string };
  pending_quotation_details?: {
    reason?: string;
    reason_status_id?: number | string;
    status_code?: string;
  };
  unique_quotation_no?: string;
  total_amount?: number;
  lead_from?: string;
  created_at?: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const OrderReport: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<Quotation[]>([]);
  const [formDataPendingQuotation, setFormDataPendingQuotation] =
    useState<PendingQuotationFormData>({
      current_follow_date: "",
      reason: "",
      reason_status_id: null,
    });

  const [query, setQuery] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return 5;
    }
    return 10;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters requested:
  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<number[]>([]);
  const [customerFilter, setCustomerFilter] = useState<number[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  // Dropdown lists
  const [descReason, setDescReason] = useState<string | null>(null);

  // Export/polling state
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchList = useCallback(
    async ({ page = 1, perPage = 10, search = "" }) => {
      setLoading(true);
      if (search) {
        setLoadingSearch(true);
      }
      try {
        const result = await apiRequestCall("post", backendRoute.getOrder, {
          data: {
            page,
            per_page: perPage,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            search: searchText.trim(),
            owner_list: ownerFilter,
            branch_list: branchFilter,
            principal_list: principalFilter,
            currency_list: currencyFilter,
            company_list: customerFilter,
          },
        });

        const res = result?.data?.data;
        if (res) {
          setList(res.data || []);
          setCurrentPage(res.current_page || page);
          setPerPage(res.per_page || perPage);
          setTotalItems(res.total || 0);
        } else {
          setList([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        setList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
        setLoadingSearch(false);
      }
    },
    [
      backendRoute.getOrder,
      searchText,
      startDate,
      endDate,
      ownerFilter,
      branchFilter,
      principalFilter,
      currencyFilter,
      customerFilter,
    ],
  );

  /* ---------- Export with polling ---------- */
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  const searchTextData = (text: string) => {
    setSearchText(text);
  };

  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = useCallback(async () => {
    try {
      setExporting(true);
      setDownloading(false);

      const result = await apiRequestCall("post", backendRoute.getOrder, {
        data: {
          search: searchText,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          owner_list: ownerFilter,
          branch_list: branchFilter,
          principal_list: principalFilter,
          currency_list: currencyFilter,
          company_list: customerFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  }, [
    backendRoute.getOrder,
    searchText,
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    customerFilter,
    pollForFile,
  ]);

  useEffect(() => {
    fetchList({ page: currentPage, perPage, search: searchText });
  }, [
    searchText,
    startDate,
    endDate,
    currentPage,
    perPage,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    customerFilter,
  ]);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data,
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "company_details.company_name", label: "Customer Name" },
      { key: "unique_order_no", label: "Order No" },
      { key: "customer_order_no", label: "Customer Order No" },
      { key: "total_amount", label: "Total" },
      { key: "lead_from", label: "Lead From" },
      { key: "branch_details.name", label: "Branch" },
      { key: "created_at", label: "Created Date" },
    ],
    [currentPage, perPage],
  );

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title mb-0">
                  Orders Report
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>
              <div className="col-12 col-md-4">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <Search
                      placeholder="Enter Text ..."
                      value={query}
                      onChange={setQuery}
                      searchText={searchTextData}
                      loading={loadingSearch}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.["order_report"]?.["export_order_report"] && (
                    <Export
                      options={[
                        {
                          label: downloading
                            ? "Downloading..."
                            : exporting
                              ? "Generating..."
                              : "Export",
                          iconClass: downloading
                            ? "ti ti-download"
                            : exporting
                              ? "ti ti-loader"
                              : "ti ti-file-type-xls",
                          colorClass:
                            exporting || downloading
                              ? "text-muted"
                              : "text-primary",
                          onClick:
                            exporting || downloading ? undefined : exportFile,
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls card */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <span className="form-icon" aria-hidden>
                      <i className="ti ti-calendar" />
                    </span>
                    <DateRangePicker
                      initialSettings={{
                        autoUpdateInput: false,
                        locale: { cancelLabel: "Clear" },
                        ...defaulSetDateFilter,
                      }}
                      onCallback={(start, end) => {
                        setStartDate(start.format("YYYY-MM-DD"));
                        setEndDate(end.format("YYYY-MM-DD"));
                      }}
                      onCancel={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      <input
                        type="text"
                        className="form-control bookingrange"
                        placeholder="Select Date Range Filter"
                        value={
                          startDate && endDate
                            ? `${dayjs(startDate).format(
                                "DD-MM-YYYY",
                              )} - ${dayjs(endDate).format("DD-MM-YYYY")}`
                            : ""
                        }
                        readOnly
                        style={{ cursor: "pointer" }}
                        aria-label="Select date range"
                      />
                    </DateRangePicker>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Owner"
                  endpoint="getOwnerDD"
                  selected={ownerFilter}
                  onChange={(v) => setOwnerFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Principal"
                  endpoint="getPrincipalDD"
                  selected={principalFilter}
                  onChange={(v) => setPrincipalFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Branch"
                  endpoint="getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Currency"
                  endpoint="getCurrencyDD"
                  selected={currencyFilter}
                  onChange={(v) => setCurrencyFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? <Loader /> : <Table data={list} columns={columns} />}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => setCurrentPage(p)}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Reason modal (basic stub) */}
        <div
          className="modal fade"
          id="messageModal"
          tabIndex={-1}
          role="dialog"
          aria-hidden="true"
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Order Reason</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <pre style={{ whiteSpace: "pre-wrap" }}>
                  {descReason || "No reason available."}
                </pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-soft-danger"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>{" "}
    </div>
  );
};

export default OrderReport;
